import React, { useState, useEffect } from 'react';
import { Modal, Input, Form, Tooltip } from 'antd';
import { SimpleSecurityDefinitionAdminDto, SimpleSecurityUserAdminDto } from '../../../services/ApiTypes';
import './UserEditModal.css';

interface EditSimpleSecurityModalProps {
  onClose: () => void;
  onSave: (updatedData: SimpleSecurityUserAdminDto) => void;
  selectedUser: SimpleSecurityUserAdminDto | undefined;
  simpleSecurityDefinition: SimpleSecurityDefinitionAdminDto;
}

const defaultUserData: SimpleSecurityUserAdminDto = {
  id: null,
  name: '',
  surname: '',
  email: '',
  phoneNumber: '',
  additionalOne: '',
  additionalTwo: '',
};

const EditSimpleSecurityModal: React.FC<EditSimpleSecurityModalProps> = ({
  onClose,
  onSave,
  selectedUser,
  simpleSecurityDefinition
}) => {
  const [form] = Form.useForm();
  const [formData, setFormData] = useState<SimpleSecurityUserAdminDto>(selectedUser || defaultUserData);

  useEffect(() => {
    setFormData(selectedUser || defaultUserData);
    form.setFieldsValue(selectedUser || defaultUserData);
  }, [selectedUser, form, simpleSecurityDefinition]);

  const handleInputChange = (key: keyof SimpleSecurityUserAdminDto, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    form.validateFields().then(() => {
      onSave(formData);
      onClose();
    }).catch(() => {
    });
  };
  const renderLabel = (label: string) => {
    return label.length > 23 ? (
      <Tooltip title={label}>
        {label.slice(0, 20) + '...'}
      </Tooltip>
    ) : (
      label
    );
  };
  return (
    <Modal
      title={!selectedUser?.id ? "New User": "Edit User"}
      open={true}
      onCancel={onClose}
      onOk={handleSave}
      okText="Ok"
      cancelText="Cancel"
      width={550}
      className='custom-modal'
    >
      <Form 
        form={form} 
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        layout="horizontal"
      >
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.name || "Name")}
          name="name"
          initialValue={formData.name}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.name,
              message: 'Required',
            },
          ]}
        >
          <Input
            value={formData.name}
            disabled={!simpleSecurityDefinition.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            onBlur={() => form.validateFields(['name'])}
          />
        </Form.Item>
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.surname || "Surname")}
          name="surname"
          initialValue={formData.surname}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.surname,
              message: 'Required',
            },
          ]}
        >
          <Input
            value={formData.surname}
            disabled={!simpleSecurityDefinition.surname}
            onChange={(e) => handleInputChange('surname', e.target.value)}
            onBlur={() => form.validateFields(['surname'])}
          />
        </Form.Item>
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.email || "Email")}
          name="email"
          initialValue={formData.email}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.email,
              message: 'Required',
            },
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input
            value={formData.email}
            disabled={!simpleSecurityDefinition.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={() => form.validateFields(['email'])}
          />
        </Form.Item>
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.phoneNumber || "Phone Number")}
          name="phoneNumber"
          initialValue={formData.phoneNumber}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.phoneNumber,
              message: 'Required',
            },
          ]}
        >
          <Input
            value={formData.phoneNumber}
            disabled={!simpleSecurityDefinition.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            onBlur={() => form.validateFields(['phoneNumber'])}
          />
        </Form.Item>
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.additionalOne || "Additional One")}
          name="additionalOne"
          initialValue={formData.additionalOne}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.additionalOne && simpleSecurityDefinition.additionalOne !== '',
              message: 'Required',
            },
          ]}
        >
          <Input
            value={formData.additionalOne}
            disabled={!simpleSecurityDefinition.additionalOne}
            onChange={(e) => handleInputChange('additionalOne', e.target.value)}
            onBlur={() => form.validateFields(['additionalOne'])}
          />
        </Form.Item>
        <Form.Item
          label={renderLabel(simpleSecurityDefinition.additionalTwo || "Additional Two")}
          name="additionalTwo"
          initialValue={formData.additionalTwo}
          validateTrigger="onBlur"
          rules={[
            {
              required: !!simpleSecurityDefinition.additionalTwo,
              message: 'Required',
            },
          ]}
        >
          <Input
            value={formData.additionalTwo}
            disabled={!simpleSecurityDefinition.additionalTwo}
            onChange={(e) => handleInputChange('additionalTwo', e.target.value)}
            onBlur={() => form.validateFields(['additionalTwo'])}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditSimpleSecurityModal;